import { HelpCircle } from 'lucide-react';
import { type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, useId, useState } from 'react';

interface BaseProps {
  error?: string;
  warning?: string;
}

export function Label({
  children,
  htmlFor,
  required,
  error,
}: {
  children: ReactNode;
  htmlFor?: string;
  required?: boolean;
  error?: string;
}) {
  return (
    <label htmlFor={htmlFor} className={`block text-sm font-medium mb-1 ${error ? 'text-red-600' : 'text-gray-700'}`}>
      {children} {required ? <span className='text-red-500'>*</span> : null}
    </label>
  );
}

export function Input({ error, warning, className, id, ...props }: InputHTMLAttributes<HTMLInputElement> & BaseProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const errorId = `${inputId}-error`;
  const hasWarning = Boolean(warning) && !error;
  const borderClass = error
    ? 'border-red-500 focus:ring-red-500'
    : hasWarning
      ? 'border-amber-400 focus:ring-amber-400 focus:border-amber-400'
      : 'border-gray-300 focus:ring-primary focus:border-primary';
  return (
    <div className='w-full'>
      <input
        {...props}
        id={inputId}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={error ? errorId : undefined}
        className={`w-full px-3.5 py-2.5 bg-white text-gray-900 border rounded-md shadow-sm focus:ring-1 focus:outline-none ${borderClass} ${className ?? ''}`}
      />
      {error ? (
        <p id={errorId} className='mt-1 text-xs text-red-600' role='alert'>
          {error}
        </p>
      ) : null}
      {!error && warning ? <p className='mt-1 text-xs text-amber-600'>{warning}</p> : null}
    </div>
  );
}

export function Select({ error, className, id, ...props }: SelectHTMLAttributes<HTMLSelectElement> & BaseProps) {
  const generatedId = useId();
  const selectId = id ?? generatedId;
  const errorId = `${selectId}-error`;
  return (
    <div className='w-full'>
      <select
        {...props}
        id={selectId}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={error ? errorId : undefined}
        className={`w-full px-3.5 py-2.5 bg-white text-gray-900 border rounded-md shadow-sm focus:ring-1 focus:outline-none ${
          error ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-primary focus:border-primary'
        } ${className ?? ''}`}
      />
      {error ? (
        <p id={errorId} className='mt-1 text-xs text-red-600' role='alert'>
          {error}
        </p>
      ) : null}
    </div>
  );
}

export function SectionTitle({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className='mb-6 border-b pb-2'>
      <h2 className='text-2xl font-bold text-gray-900'>{title}</h2>
      {subtitle ? <p className='text-sm text-gray-500 mt-1'>{subtitle}</p> : null}
    </div>
  );
}

export function Checkbox({
  label,
  checked,
  onChange,
  error,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  error?: string;
  disabled?: boolean;
}) {
  const id = useId();
  const errorId = `${id}-error`;
  return (
    <div className='flex flex-col'>
      <label className={`flex items-start ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
        <input
          type='checkbox'
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          disabled={disabled}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={error ? errorId : undefined}
          className={`h-4 w-4 mt-0.5 rounded border-gray-300 text-primary focus:ring-primary ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
        />
        <span className={`ml-2 text-sm ${error ? 'text-red-600' : 'text-gray-700'}`}>{label}</span>
      </label>
      {error ? (
        <p id={errorId} className='mt-1 text-xs text-red-600' role='alert'>
          {error}
        </p>
      ) : null}
    </div>
  );
}

export function RadioGroup({
  name,
  value,
  options,
  onChange,
  error,
  inline = false,
}: {
  name: string;
  value: string | boolean | undefined;
  options: Array<{ value: string | boolean; label: string }>;
  onChange: (value: string | boolean) => void;
  error?: string;
  inline?: boolean;
}) {
  return (
    <div className={`${inline ? 'flex gap-4' : 'space-y-2'} mt-2`}>
      {options.map((option) => (
        <label key={String(option.value)} className='flex items-center cursor-pointer'>
          <input
            type='radio'
            name={name}
            checked={value === option.value}
            onChange={() => onChange(option.value)}
            className='h-4 w-4 text-primary focus:ring-primary border-gray-300'
          />
          <span className={`ml-2 text-sm ${error ? 'text-red-600' : 'text-gray-700'}`}>{option.label}</span>
        </label>
      ))}
      {error ? <p className='text-xs text-red-600 mt-1'>{error}</p> : null}
    </div>
  );
}

export function MultiSelect({
  options,
  selected,
  onChange,
  error,
  columns = 3,
}: {
  options: Array<{ value: string; label: string }>;
  selected: string[];
  onChange: (selected: string[]) => void;
  error?: string;
  columns?: 1 | 2 | 3 | 4;
}) {
  const toggle = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const gridCols = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3',
    4: 'grid-cols-2 sm:grid-cols-4',
  };

  return (
    <div>
      <div className={`grid ${gridCols[columns]} gap-2 mt-2`}>
        {options.map((option) => (
          <label
            key={option.value}
            className={`border p-3 rounded cursor-pointer transition ${
              selected.includes(option.value) ? 'bg-blue-50 border-primary' : 'hover:bg-gray-50 bg-white'
            }`}>
            <input
              type='checkbox'
              className='sr-only'
              checked={selected.includes(option.value)}
              onChange={() => toggle(option.value)}
            />
            <div className='font-medium text-center text-sm'>{option.label}</div>
          </label>
        ))}
      </div>
      {error ? <p className='text-xs text-red-600 mt-1'>{error}</p> : null}
    </div>
  );
}

export function WhyWeAsk({ text }: { text: string }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <span className='relative inline-flex items-center ml-1'>
      <button
        type='button'
        onClick={() => setIsOpen(!isOpen)}
        className='text-gray-400 hover:text-primary transition'
        aria-label='Why we ask this'>
        <HelpCircle size={14} />
      </button>
      {isOpen ? (
        <span className='absolute left-6 top-0 z-20 w-64 p-2 bg-white border border-gray-200 rounded-md shadow-lg text-xs text-gray-600 leading-relaxed'>
          {text}
        </span>
      ) : null}
    </span>
  );
}

export function RangeSlider({
  value,
  onChange,
  min = 1,
  max = 5,
  labels,
  error,
  'aria-label': ariaLabel,
}: {
  'value': number;
  'onChange': (value: number) => void;
  'min'?: number;
  'max'?: number;
  'labels'?: { min: string; max: string };
  'error'?: string;
  'aria-label'?: string;
}) {
  return (
    <div className='mt-2'>
      <div className='flex items-center gap-4'>
        <input
          type='range'
          min={min}
          max={max}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          aria-label={ariaLabel}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={value}
          className='w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary'
        />
        <span className='text-sm font-medium text-gray-700 w-8 text-center' aria-hidden='true'>
          {value}
        </span>
      </div>
      {labels ? (
        <div className='flex justify-between text-xs text-gray-500 mt-1' aria-hidden='true'>
          <span>{labels.min}</span>
          <span>{labels.max}</span>
        </div>
      ) : null}
      {error ? (
        <p className='text-xs text-red-600 mt-1' role='alert'>
          {error}
        </p>
      ) : null}
    </div>
  );
}
